import React, { useState, useMemo } from 'react';
import { TextField, InputAdornment, Box, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { matchKorean, getChosung } from '../../../../utils/methods';
import SellerShareSelect from '../../../common/SellerShareSelect';
import RegisterTypeToggle from './RegisterTypeToggle';
import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import CustomRadioBox from '../../../CustomRadioBox';
import { getBranchProductsById } from '../../../../api/point';
import PointUsageFields from './PointUsageFields';

import {
  FormInputWrapperFull,
  StyledFormControlLabel,
  textInputStyle,
  disabledInputStyle,
  dateInputStyleFlex,
  ErrorFieldWrapper,
  ContentSection,
  FormFieldGroup,
  FormFieldGroupHalf,
  FormFieldRow,
  FormFieldLabel,
  BasePriceHint,
} from './EnterSaleInfoModalStyled';

// InputAdornment 스타일
const adornmentStyle = { color: '#737373', fontSize: '13px', fontWeight: 500 };

const EnterSaleInfoModalProductSection = ({
  control,
  setValue,
  watch,
  branchManager,
  productType,
  // 라커 연장 진입 — 라커 외 상품 종류를 감춘다
  lockerOnly = false,
  setProductType,
  productPrice,
  productDiscountAmount,
  productFinalPrice,
  productOutstandingPrice,
  productQuantity,
  getProductPrice,
  resetProductFields,
  changeProductPrice,
  productBasePrice,
  changeProductFinalPrice,
  changeProductOutstandingPrice,
  changeProductPaidPrice,
  changeProductQuantity,
  formatInteger,
  errorFields,
  setErrorFields,
  isPointUsageEnabled,
  availablePoint,
  minUsePoint,
  usablePointLimit,
}) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const [productPanelOpen, setProductPanelOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const { data: ProductList } = useQuery({
    queryKey: ['EnterSaleProductList', user?.branch?.branchId],
    queryFn: () => getBranchProductsById(user?.token, user?.branch?.branchId, 'page=1&limit=1000000'),
    enabled: !!user?.token && !!user?.branch?.branchId,
  });

  // 상품 필터링: LOCKER는 productCategoryType으로, 나머지는 productType으로 구분
  const filteredProducts = useMemo(() => {
    let filtered = ProductList?.content?.filter(item => {
      if (productType === 'LOCKER') return item.productCategoryType === 'LOCKER';
      if (productType === 'PERIOD') return item.productType === 'PERIOD' && item.productCategoryType !== 'LOCKER';
      return item.productType === productType;
    }) || [];
    if (productSearch.trim()) {
      filtered = filtered.filter(item => matchKorean(item.productName || '', productSearch.trim()));
    }
    const charOrder = c => {
      if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(c)) return 0;
      if (/[a-zA-Z]/.test(c)) return 1;
      if (/[0-9]/.test(c)) return 2;
      return 3;
    };
    filtered.sort((a, b) => {
      const aFirst = (a.productName || '')[0] || '';
      const bFirst = (b.productName || '')[0] || '';
      const orderDiff = charOrder(aFirst) - charOrder(bFirst);
      if (orderDiff !== 0) return orderDiff;
      return (a.productName || '').localeCompare(b.productName || '', 'ko');
    });
    return filtered;
  }, [ProductList, productType, productSearch]);

  return (
    <ContentSection>
      {/* #124 신규등록/재등록 — 라디오 카드. 기본값은 서버 자동 판정, 바꾸면 그 값이 저장된다 */}
      <RegisterTypeToggle control={control} setValue={setValue} />

      {/* 판매일시 + 판매 담당자 */}
      <FormFieldRow>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('판매일시')}</FormFieldLabel>
          <FormInputDate
            name="saleDate"
            control={control}
            placeholder={t('판매일(오늘)')}
            renderInput={params => (
              <TextField color="info" size="small" variant="outlined" {...params} sx={{ ...dateInputStyleFlex, width: '100%' }} />
            )}
          />
        </FormFieldGroupHalf>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('판매 담당자')}</FormFieldLabel>
          <ErrorFieldWrapper hasError={errorFields?.branchManager}>
            {/* #122 판매 담당자 N명 + 귀속 비율 — 담당 강사 다중 선택과 같은 구성 */}
            <SellerShareSelect
              control={control}
              setValue={setValue}
              name="branchManager"
              sharesName="sellerShares"
              options={branchManager}
              placeholder={t('담당자 선택')}
              onChange={() => setErrorFields(prev => ({ ...prev, branchManager: false }))}
            />
          </ErrorFieldWrapper>
        </FormFieldGroupHalf>
      </FormFieldRow>

      {/* 상품 선택 (라커 / 기간 상품 / 재고 상품).
          라커 연장으로 들어온 경우엔 라커만 팔 수 있어 종류 선택 자체를 숨긴다. */}
      {!lockerOnly && (
      <FormFieldGroup>
        <FormFieldLabel>{t('상품 선택')}</FormFieldLabel>
        <FormInputWrapperFull>
          <StyledFormControlLabel
            control={
              <CustomRadioBox
                checked={productType === 'LOCKER'}
                onChange={() => {
                  setProductType('LOCKER');
                  resetProductFields();
                }}
              />
            }
            label={t('라커')}
          />
          <StyledFormControlLabel
            control={
              <CustomRadioBox
                checked={productType === 'PERIOD'}
                onChange={() => {
                  setProductType('PERIOD');
                  resetProductFields();
                }}
              />
            }
            label={t('기간 상품')}
          />
          <StyledFormControlLabel
            control={
              <CustomRadioBox
                checked={productType === 'STOCK'}
                onChange={() => {
                  setProductType('STOCK');
                  resetProductFields();
                }}
              />
            }
            label={t('재고 상품')}
          />
        </FormInputWrapperFull>
      </FormFieldGroup>
      )}

      {/* 상품명 */}
      <FormFieldGroup>
        <FormFieldLabel>{t('상품명')}</FormFieldLabel>
        <ErrorFieldWrapper hasError={errorFields?.productId}>
          <Box sx={{ width: '100%' }}>
            {/* 헤더 */}
            <Box
              onClick={() => { setProductPanelOpen(prev => !prev); setProductSearch(''); }}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                height: '44px', px: '14px',
                border: productPanelOpen ? '1.5px solid #0F7F85' : '1px solid #c4c4c4',
                borderRadius: productPanelOpen ? '6px 6px 0 0' : '6px',
                cursor: 'pointer',
                backgroundColor: watch('productId') ? '#f0f8f8' : '#fff',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: '#0F7F85' },
              }}>
              <Typography sx={{ fontSize: '14px', fontWeight: watch('productId') ? 500 : 400, color: watch('productId') ? '#0F7F85' : '#999' }}>
                {watch('productId')
                  ? (() => {
                      const p = filteredProducts.find(i => i.productId === watch('productId'))
                        || ProductList?.content?.find(i => i.productId === watch('productId'));
                      if (!p) return '선택됨';
                      const periodUnit = p.periodType === 'MONTH' ? '개월' : '일';
                      return p.periodValue ? `${p.productName} (${p.periodValue}${periodUnit})` : p.productName;
                    })()
                  : '상품을 선택해 주세요'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', backgroundColor: productPanelOpen ? '#0F7F85' : 'transparent', transition: 'all 0.15s ease' }}>
                {productPanelOpen ? <KeyboardArrowUpIcon sx={{ color: '#fff', fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />}
              </Box>
            </Box>

            {/* 패널 */}
            {productPanelOpen && (
              <Box sx={{ border: '1.5px solid #0F7F85', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                {/* 검색 */}
                <Box sx={{ p: '10px 12px', borderBottom: '1px solid #eee' }}>
                  <TextField
                    size="small"
                    placeholder="상품 검색"
                    value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                    sx={{ width: '100%', '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment> }}
                  />
                </Box>

                {/* 리스트 */}
                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                  {filteredProducts.length === 0 ? (
                    <Box sx={{ p: '24px', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '13px', color: '#999' }}>검색 결과가 없습니다</Typography>
                    </Box>
                  ) : (
                    (() => {
                      let lastCho = '';
                      return filteredProducts.map(item => {
                        const firstChar = (item.productName || '')[0] || '';
                        const cho = getChosung(firstChar);
                        const showHeader = cho !== lastCho;
                        lastCho = cho;
                        const isSelected = watch('productId') === item.productId;
                        const periodUnit = item.periodType === 'MONTH' ? '개월' : '일';
                        const subText = item.periodValue ? `${item.periodValue}${periodUnit}` : item.stockQuantity ? `${item.stockQuantity}개` : '';
                        return (
                          <React.Fragment key={item.productId}>
                            {showHeader && (
                              <Box sx={{ px: '14px', py: '4px', backgroundColor: '#f7f7f7', borderBottom: '1px solid #eee' }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#999' }}>{cho}</Typography>
                              </Box>
                            )}
                            <Box
                              onClick={() => {
                                setValue('productId', item.productId);
                                getProductPrice(item.productId, item.price);
                                setErrorFields(prev => ({ ...prev, productId: false }));
                                if (item.periodValue && item.periodType) {
                                  const startDate = watch('courseStartDate') || new Date();
                                  let endDate;
                                  if (item.periodType === 'MONTH') {
                                    endDate = dayjs(startDate).add(item.periodValue, 'month').toDate();
                                  } else if (item.periodType === 'DAY') {
                                    endDate = dayjs(startDate).add(item.periodValue, 'day').toDate();
                                  }
                                  if (endDate) setValue('courseEndDate', endDate);
                                }
                                setProductPanelOpen(false);
                                setProductSearch('');
                              }}
                              sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: '14px', py: '10px', cursor: 'pointer',
                                backgroundColor: isSelected ? '#f0f8f8' : '#fff',
                                borderBottom: '1px solid #f5f5f5',
                                '&:hover': { backgroundColor: isSelected ? '#e6f5f5' : '#f5f5f5' },
                                '&:last-child': { borderBottom: 'none' },
                              }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                                {isSelected && <CheckCircleIcon sx={{ fontSize: 18, color: '#0F7F85', flexShrink: 0 }} />}
                                <Typography sx={{ fontSize: '13px', color: '#333', fontWeight: isSelected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {item.productName}
                                </Typography>
                                {subText && (
                                  <Typography sx={{ fontSize: '12px', color: '#999', flexShrink: 0 }}>
                                    {subText}
                                  </Typography>
                                )}
                              </Box>
                              {/* 상품 금액 — 일반상품·라커는 단가 하나만 있다. */}
                              {item.price > 0 && (
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0F7F85', flexShrink: 0, ml: '8px' }}>
                                  {formatInteger(item.price)}원
                                </Typography>
                              )}
                            </Box>
                          </React.Fragment>
                        );
                      });
                    })()
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </ErrorFieldWrapper>
      </FormFieldGroup>

      {/* 판매금액 / 할인금액 (2 columns) */}
      <FormFieldRow>
        <FormFieldGroupHalf>
          <FormFieldLabel>
            {t('판매금액')}
            {productBasePrice > 0 && formatInteger(productPrice) !== formatInteger(productBasePrice) && (
              <BasePriceHint>정가 {formatInteger(productBasePrice)}원</BasePriceHint>
            )}
          </FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeProductPrice(e.target.value)}
            disabled={!watch('productId')}
            color="info"
            sx={!watch('productId') ? disabledInputStyle : textInputStyle}
            variant="outlined"
            name="productPrice"
            size="small"
            staticValue={formatInteger(productPrice)}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('할인금액')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeProductFinalPrice(e.target.value)}
            color="info"
            sx={textInputStyle}
            variant="outlined"
            name="productDiscountAmount"
            size="small"
            value={productDiscountAmount}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
      </FormFieldRow>

      {/* 보유 포인트 / 사용할 포인트 (정식회원 + 지점 포인트 사용 설정 on 일 때만) */}
      <PointUsageFields
        isVisible={isPointUsageEnabled}
        control={control}
        availablePoint={availablePoint}
        minUsePoint={minUsePoint}
        usablePointLimit={usablePointLimit}
        errorFields={errorFields}
        setErrorFields={setErrorFields}
        formatInteger={formatInteger}
      />

      {/* 미수금 / 결제금액 (2 columns) */}
      <FormFieldRow>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('미수금')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeProductOutstandingPrice(e.target.value)}
            color="info"
            sx={{
              ...textInputStyle,
              '& .MuiOutlinedInput-input': {
                ...textInputStyle['& .MuiOutlinedInput-input'],
                color: '#ee735a !important',
              },
            }}
            variant="outlined"
            name="productOutstandingPrice"
            size="small"
            value={formatInteger(productOutstandingPrice)}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('결제금액')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeProductPaidPrice(e.target.value)}
            color="info"
            sx={textInputStyle}
            variant="outlined"
            name="productFinalPrice"
            size="small"
            value={formatInteger(productFinalPrice)}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
      </FormFieldRow>

      {/* 판매수량 (재고 상품일 때만) */}
      {productType === 'STOCK' && (
        <FormFieldGroup>
          <FormFieldLabel>{t('판매수량')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeProductQuantity(e.target.value)}
            color="info"
            sx={textInputStyle}
            variant="outlined"
            name="productQuantity"
            size="small"
            value={productQuantity}
            placeholder={t('판매수량')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('개')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroup>
      )}
    </ContentSection>
  );
};

export default EnterSaleInfoModalProductSection;
